/**
 * The building outlines: walls rasterised, door-width gaps closed, interiors filled, and each
 * connected piece taken as one building.
 */

import type { CellPoint, GridShape, Mask } from "./grid.ts";
import { close, components, dilate, emptyMask, fillHoles, rasterSegment } from "./grid.ts";
import type { Segment } from "./input.ts";
import type { PlanPoint } from "./types.ts";

export interface Raster {
  readonly shape: GridShape;
  /** Cell size, model inches. */
  readonly cell: number;
  /** Model coordinates of cell (0, 0)'s lower-left corner. */
  readonly ox: number;
  readonly oy: number;
}

/** Above this the cell size grows rather than the grid. */
const MAX_CELLS = 6_000_000;

export function rasterFor(segments: readonly Segment[], preferredCell: number, padInches: number): Raster | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { a, b } of segments) {
    minX = Math.min(minX, a.x, b.x);
    minY = Math.min(minY, a.y, b.y);
    maxX = Math.max(maxX, a.x, b.x);
    maxY = Math.max(maxY, a.y, b.y);
  }
  if (!Number.isFinite(minX) || maxX - minX <= 0 || maxY - minY <= 0) return null;
  let cell = preferredCell;
  const cellsFor = (c: number) =>
    Math.ceil((maxX - minX + 2 * padInches) / c) * Math.ceil((maxY - minY + 2 * padInches) / c);
  while (cellsFor(cell) > MAX_CELLS) cell *= 1.5;
  return {
    shape: {
      width: Math.ceil((maxX - minX + 2 * padInches) / cell),
      height: Math.ceil((maxY - minY + 2 * padInches) / cell),
    },
    cell,
    ox: minX - padInches,
    oy: minY - padInches,
  };
}

export function toCell(raster: Raster, p: PlanPoint): CellPoint {
  return { x: (p.x - raster.ox) / raster.cell, y: (p.y - raster.oy) / raster.cell };
}

export function toModel(raster: Raster, p: CellPoint): PlanPoint {
  return { x: raster.ox + p.x * raster.cell, y: raster.oy + p.y * raster.cell };
}

export function cellRadius(raster: Raster, inches: number): number {
  return Math.max(1, Math.round(inches / raster.cell));
}

export function rasterSegments(raster: Raster, segments: readonly Segment[], mask?: Mask): Mask {
  const out = mask ?? emptyMask(raster.shape);
  for (const { a, b } of segments) {
    const p = toCell(raster, a);
    const q = toCell(raster, b);
    rasterSegment(out, raster.shape, p.x, p.y, q.x, q.y);
  }
  return out;
}

export interface Footprint {
  /** Walls, one cell thick plus a cell either side. */
  readonly walls: Mask;
  readonly mask: Mask;
  /** Building id per cell, -1 outside every building. */
  readonly building: Int32Array;
  readonly buildingCount: number;
  /** Pieces too small to be a building and carrying no room tag. */
  readonly droppedPieces: number;
}

export interface FootprintOptions {
  /** Gaps narrower than twice this close: door openings, storefront breaks. */
  readonly closeRadiusInches: number;
  /** A piece smaller than this, with no room tag in it, is noise rather than a building. */
  readonly minBuildingSquareInches: number;
}

export function findFootprint(
  raster: Raster,
  walls: readonly Segment[],
  tagCells: readonly number[],
  options: FootprintOptions,
): Footprint {
  const wallMask = dilate(rasterSegments(raster, walls), raster.shape, 1);
  const closed = close(wallMask, raster.shape, cellRadius(raster, options.closeRadiusInches));
  const mask = fillHoles(closed, raster.shape);
  const pieces = components(mask, raster.shape);

  const tagged = new Set<number>();
  for (const i of tagCells) {
    const id = pieces.labels[i];
    if (id !== undefined && id >= 0) tagged.add(id);
  }
  const minCells = options.minBuildingSquareInches / (raster.cell * raster.cell);
  const kept = pieces.sizes
    .map((size, id) => ({ id, size }))
    .filter(({ id, size }) => size >= minCells || tagged.has(id))
    .sort((a, b) => b.size - a.size);
  const remap = new Map(kept.map(({ id }, index) => [id, index]));

  const building = new Int32Array(mask.length).fill(-1);
  const keptMask = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) {
    const id = pieces.labels[i]!;
    if (id < 0) continue;
    const b = remap.get(id);
    if (b === undefined) continue;
    building[i] = b;
    keptMask[i] = 1;
  }
  return {
    walls: wallMask,
    mask: keptMask,
    building,
    buildingCount: kept.length,
    droppedPieces: pieces.sizes.length - kept.length,
  };
}
